// Optimized API functions with pagination and caching

import { API_BASE_URL, getAuthHeader } from './api'
import type { ResearchDepth } from '@/types/intelligence'
import { apiCache, cacheKeys, cachedApiCall } from './cache'
import { getCurrentUserId } from './getUserId'

export interface PaginatedResponse<T> {
  total: number
  limit: number
  offset: number
  results: T[]
  has_more?: boolean
}

export type ResearchListBucket = 'all' | 'completed' | 'in_progress' | 'drafts'

export interface ResearchListCounts {
  completed: number
  in_progress: number
  drafts: number
}

export interface UserResearchPaginatedResponse<T> extends PaginatedResponse<T> {
  counts?: ResearchListCounts
  bucket?: ResearchListBucket
}

export interface ResearchSummary {
  id: number
  title: string
  query: string
  status: string
  created_at: string
  updated_at?: string
  summary?: string
  report_length?: number
  has_results: boolean
  is_public: boolean
  author_name?: string
  author_avatar?: string
  author_email?: string
  image_url?: string
  key_findings?: string[]
  sources?: any[]
  sources_count?: number
  category?: string
  view_count?: number
  tags?: string[]
  model?: string
  report_text?: string
  depth?: ResearchDepth
}

// Note: Using the shared cache utility from ./cache.ts

export async function fetchUserResearchOptimized(
  limit: number = 20,
  offset: number = 0,
  includePrivate: boolean = true,
  cacheBuster: string = '',
  bucket: ResearchListBucket = 'all'
): Promise<UserResearchPaginatedResponse<ResearchSummary>> {
  // Skip cache if cache buster is provided
  if (cacheBuster) {
    try {
      const queryParams = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
        include_private: includePrivate.toString(),
        bucket,
        _t: cacheBuster
      })
      
      // Use the optimized endpoint
      const response = await fetch(`${API_BASE_URL}/research/my/optimized?${queryParams}`, {
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        }
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      return await response.json()
    } catch (error) {
      console.error('Error fetching optimized user research:', error)
      throw error
    }
  }
  
  // Use cached API call for normal requests
  // Get the current user ID
  const userId = getCurrentUserId()
  
  // If no user ID, skip caching and fetch directly
  if (!userId) {
    console.warn('No user ID found, fetching without cache')
    const queryParams = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
      include_private: includePrivate.toString(),
      bucket,
    })
    
    const response = await fetch(`${API_BASE_URL}/research/my/optimized?${queryParams}`, {
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader()
      }
    })
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    
    return await response.json()
  }
  
  const cacheKey = cacheKeys.userResearch(userId, limit, offset, includePrivate, bucket)
  
  return cachedApiCall(cacheKey, async () => {
    const queryParams = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
      include_private: includePrivate.toString(),
      bucket,
    })
    
    const response = await fetch(`${API_BASE_URL}/research/my/optimized?${queryParams}`, {
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader()
      }
    })
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    
    return await response.json()
  }, 5 * 60 * 1000) // Cache for 5 minutes
}

export async function fetchPublicResearchOptimized(
  limit: number = 20,
  offset: number = 0,
  search?: string,
  authorEmail?: string,
  dateFrom?: string
): Promise<PaginatedResponse<ResearchSummary>> {
  // Use cached API call
  const cacheKey = cacheKeys.publicResearch(limit, offset)
  
  return cachedApiCall(cacheKey, async () => {
    const queryParams = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString()
    })
    
    if (search) queryParams.append('search', search)
    if (authorEmail) queryParams.append('author_email', authorEmail)
    if (dateFrom) queryParams.append('date_from', dateFrom)
    
    const response = await fetch(`${API_BASE_URL}/research/public/optimized?${queryParams}`, {
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader()
      }
    })
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    
    return await response.json()
  }, 5 * 60 * 1000) // Cache for 5 minutes
}

export async function fetchResearchFullContent(researchId: number): Promise<any> {
  const cacheKey = cacheKeys.researchDetail(researchId)
  
  // Check cache
  const cached = apiCache.get(cacheKey)
  if (cached) {
    return cached
  }
  
  try {
    const response = await fetch(`${API_BASE_URL}/api/v2/research/${researchId}/full`, {
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader()
      }
    })
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    
    const data = await response.json()
    apiCache.set(cacheKey, data, 10 * 60 * 1000) // Cache for 10 minutes
    return data
  } catch (error) {
    console.error('Error fetching research full content:', error)
    throw error
  }
}

// Fetch user avatar separately (cached)
export async function fetchUserAvatar(userId: number): Promise<string | null> {
  const cacheKey = `avatar_${userId}`
  
  return cachedApiCall(cacheKey, async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/user/${userId}/avatar`, {
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        }
      })
      
      if (!response.ok) {
        console.warn(`Failed to fetch avatar for user ${userId}`)
        return null
      }
      
      const data = await response.json()
      return data.avatar_url || null
    } catch (error) {
      console.error(`Error fetching avatar for user ${userId}:`, error)
      return null
    }
  }, 60 * 60 * 1000) // Cache avatars for 1 hour
}

// Fetch full report text separately
export async function fetchReportText(researchId: number): Promise<string | null> {
  const cacheKey = `report_${researchId}`
  
  return cachedApiCall(cacheKey, async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/research/${researchId}/report`, {
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        }
      })
      
      if (!response.ok) {
        console.warn(`Failed to fetch report for research ${researchId}`)
        return null
      }
      
      const data = await response.json()
      return data.report_text || null
    } catch (error) {
      console.error(`Error fetching report for research ${researchId}:`, error)
      return null
    }
  }, 30 * 60 * 1000) // Cache reports for 30 minutes
}

export async function fetchResearchBatch(researchIds: number[]): Promise<ResearchSummary[]> {
  if (researchIds.length === 0) return []
  
  try {
    const response = await fetch(`${API_BASE_URL}/api/v2/research/batch-summary`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader()
      },
      body: JSON.stringify({ research_ids: researchIds })
    })
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    
    return await response.json()
  } catch (error) {
    console.error('Error fetching research batch:', error)
    return []
  }
}

// Clear cache when research is updated
export function invalidateResearchCache(researchId?: number): void {
  if (researchId) {
    // Clear specific research cache
    apiCache.invalidate(cacheKeys.researchDetail(researchId))
  }
  
  // Clear all research-related caches
  apiCache.invalidatePattern(/^(public_research_|user_research_|research_detail_)/)
}

// Preload next page
export async function preloadNextPage(
  currentOffset: number,
  limit: number,
  fetchFunction: (limit: number, offset: number) => Promise<any>
): Promise<void> {
  try {
    await fetchFunction(limit, currentOffset + limit)
  } catch (error) {
    // Ignore preload errors
    console.debug('Preload failed:', error)
  }
}